/**
 * Framework-agnostic roving-focus core (WAI-ARIA roving tabindex pattern).
 *
 * Items are kept in a map and ordered by DOM position at navigation time, so
 * registration order doesn't matter and the navigation always follows the
 * visual layout. Moving updates tabindex (optional) and DOM focus (optional)
 * and notifies via `onActiveChange`.
 */

import type { Orientation, RovingConfig, RovingCore, RovingItem } from "./types";

const PREV_KEYS: Record<Orientation, ReadonlySet<string>> = {
  vertical: new Set(["ArrowUp"]),
  horizontal: new Set(["ArrowLeft"]),
  both: new Set(["ArrowUp", "ArrowLeft"]),
};
const NEXT_KEYS: Record<Orientation, ReadonlySet<string>> = {
  vertical: new Set(["ArrowDown"]),
  horizontal: new Set(["ArrowRight"]),
  both: new Set(["ArrowDown", "ArrowRight"]),
};
const VIM_PREV: Record<Orientation, ReadonlySet<string>> = {
  vertical: new Set(["k"]),
  horizontal: new Set(["h"]),
  both: new Set(["k", "h"]),
};
const VIM_NEXT: Record<Orientation, ReadonlySet<string>> = {
  vertical: new Set(["j"]),
  horizontal: new Set(["l"]),
  both: new Set(["j", "l"]),
};

function orderByDom(map: Map<string, HTMLElement>): RovingItem[] {
  const items: RovingItem[] = [...map.entries()].map(([id, el]) => ({ id, el }));
  items.sort((a, b) => {
    if (a.el === b.el) {
      return 0;
    }
    // Bitwise masks are the documented contract of compareDocumentPosition.
    const relation = a.el.compareDocumentPosition(b.el);
    // oxlint-disable no-bitwise
    if ((relation & Node.DOCUMENT_POSITION_FOLLOWING) !== 0) {
      return -1;
    }
    if ((relation & Node.DOCUMENT_POSITION_PRECEDING) !== 0) {
      return 1;
    }
    // oxlint-enable no-bitwise
    return 0;
  });
  return items;
}

export function createRovingCore(config: RovingConfig = {}): RovingCore {
  const orientation = config.orientation ?? "vertical";
  const wrap = config.wrap ?? false;
  const focusOnChange = config.focusOnChange ?? true;
  const manageTabIndex = config.manageTabIndex ?? true;

  const elements = new Map<string, HTMLElement>();
  let activeId: string | null = null;

  const applyTabIndex = () => {
    if (!manageTabIndex) {
      return;
    }
    for (const [id, el] of elements) {
      el.tabIndex = id === activeId ? 0 : -1;
    }
  };

  const setActive: RovingCore["setActive"] = (id, options) => {
    activeId = id;
    applyTabIndex();
    if (id !== null && (options?.focus ?? focusOnChange)) {
      elements.get(id)?.focus();
    }
    config.onActiveChange?.(id);
  };

  const indexOfActive = (ordered: RovingItem[]): number =>
    ordered.findIndex((item) => item.id === activeId);

  const move: RovingCore["move"] = (delta) => {
    const ordered = orderByDom(elements);
    if (ordered.length === 0) {
      return false;
    }
    const current = indexOfActive(ordered);
    if (current === -1) {
      // Nothing active yet: entering the group lands on the edge nearest travel.
      const entry = delta > 0 ? 0 : ordered.length - 1;
      setActive(ordered[entry]?.id ?? null);
      return true;
    }
    let next = current + delta;
    if (next < 0 || next >= ordered.length) {
      if (!wrap) {
        return false;
      }
      next = (next + ordered.length) % ordered.length;
    }
    setActive(ordered[next]?.id ?? null);
    return true;
  };

  const first: RovingCore["first"] = () => {
    const ordered = orderByDom(elements);
    setActive(ordered[0]?.id ?? null);
  };
  const last: RovingCore["last"] = () => {
    const ordered = orderByDom(elements);
    setActive(ordered.at(-1)?.id ?? null);
  };

  const handleKeyDown: RovingCore["handleKeyDown"] = (event) => {
    const { key } = event;
    if (key === "Home") {
      event.preventDefault();
      first();
      return true;
    }
    if (key === "End") {
      event.preventDefault();
      last();
      return true;
    }
    const isPrev =
      PREV_KEYS[orientation].has(key) ||
      (config.vimKeys === true && VIM_PREV[orientation].has(key));
    const isNext =
      NEXT_KEYS[orientation].has(key) ||
      (config.vimKeys === true && VIM_NEXT[orientation].has(key));
    if (!isPrev && !isNext) {
      return false;
    }
    const moved = move(isNext ? 1 : -1);
    if (moved) {
      event.preventDefault();
    }
    return moved;
  };

  return {
    register(id, el) {
      elements.set(id, el);
      if (manageTabIndex) {
        el.tabIndex = id === activeId ? 0 : -1;
      }
    },
    unregister(id) {
      elements.delete(id);
      if (activeId === id) {
        activeId = null;
      }
    },
    items: () => orderByDom(elements),
    getActive: () => activeId,
    setActive,
    move,
    first,
    last,
    handleKeyDown,
  };
}
